import React, { useRef, useState, useEffect, useCallback } from 'react';
import { View, Pressable, StyleSheet, Platform, Modal, Dimensions } from 'react-native';
import { BottomSheetModal } from '@/components/residents/marketplace/shared/BottomSheetModal';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface AdaptiveMenuProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  anchor?: 'right' | 'left';
}

export const AdaptiveMenu: React.FC<AdaptiveMenuProps> = ({
  visible,
  onClose,
  children,
  anchor = 'right',
}) => {
  const { layoutMode } = useResponsive();
  const { colors } = useThemeColors();
  const anchorRef = useRef<View>(null);
  const [position, setPosition] = useState<{ top: number; left: number; right: number } | null>(null);

  const measure = useCallback(() => {
    anchorRef.current?.measureInWindow((x, y, width, height) => {
      const screenWidth = Dimensions.get('window').width;
      setPosition({
        top: y + height + 4,
        left: x,
        right: screenWidth - x - width,
      });
    });
  }, []);

  useEffect(() => {
    if (visible) {
      measure();
    } else {
      setPosition(null);
    }
  }, [visible, measure]);

  if (layoutMode === 'mobile' || layoutMode === 'tablet') {
    return (
      <BottomSheetModal visible={visible} onClose={onClose}>
        {children}
      </BottomSheetModal>
    );
  }

  return (
    <>
      <View ref={anchorRef} collapsable={false} />
      {visible && position && (
        <Modal visible transparent animationType="none" onRequestClose={onClose}>
          <View style={styles.modalRoot}>
            <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
            <View
              style={[
                styles.popover,
                { backgroundColor: colors.bg.modal, borderColor: colors.card.border },
                anchor === 'left'
                  ? { top: position.top, left: position.left }
                  : { top: position.top, right: position.right },
              ]}
            >
              {children}
            </View>
          </View>
        </Modal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  modalRoot: {
    flex: 1,
  },
  popover: {
    position: 'absolute',
    minWidth: 180,
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: 6,
    paddingHorizontal: 6,
    ...Platform.select({
      web: {
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
      },
      default: {
        elevation: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
      },
    }),
  },
});
